import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Heart } from 'lucide-react';
import { CUSTOM_ORDERS_ENABLED } from '../lib/shop';

// Pas d'icône TikTok dans lucide : tracé maison au même trait que les autres.
function TikTok(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M14 3v11.5a3.5 3.5 0 1 1-3.5-3.5" />
      <path d="M14 3c.4 2.6 2.2 4.4 5 4.6" />
    </svg>
  );
}

const SOCIAL_LINKS = [
  { label: 'Instagram', href: 'https://www.instagram.com/le.placard.a.doudous/', Icon: Instagram },
  { label: 'TikTok', href: 'https://www.tiktok.com/@le.placard.doudous', Icon: TikTok },
  { label: 'Facebook', href: 'https://www.facebook.com/people/Le-placard-%C3%A0-doudous/61594971245979/', Icon: Facebook },
  { label: 'YouTube', href: 'https://www.youtube.com/@Leplacard%C3%A0doudous', Icon: Youtube },
];
import { ReactComponent as LogoMark } from '../assets/logo-placard-doudous.svg';
import { useCopy, useLocale } from '../i18n/LocaleContext';

const COPY = {
  fr: {
    promise: ['Lire, avec un ami ', 'tout doux.'], by: 'par Margot',
    about: "Des marque-pages en tissu cousus main en France, en minkee ultra-doux. Chaque pièce est un petit trésor cousu avec soin dans l'atelier de Margot.",
    madeWith: 'Fait avec', inFrance: 'en France', shop: 'Boutique', info: 'Informations',
    shopLinks: [['Toutes les pièces', '/boutique'], ['Sur mesure', '/sur-mesure'], ['Nouveautés', '/boutique?sort=newest']],
    infoLinks: [['Notre histoire', '/a-propos'], ['Contact', '/contact'], ['Livraison & retours', '/livraison'], ['CGV', '/cgv'], ['Mentions légales', '/mentions-legales'], ['Confidentialité', '/confidentialite']],
    rights: 'Le Placard à Doudous — Tous droits réservés', secure: 'Paiement sécurisé Stripe',
  },
  en: {
    promise: ['Reading, with a ', 'cuddly friend.'], by: 'by Margot',
    about: 'Fabric bookmarks sewn by hand in France, in ultra-soft minky. Each piece is a small treasure, carefully stitched in Margot’s workshop.',
    madeWith: 'Made with', inFrance: 'in France', shop: 'Shop', info: 'Information',
    shopLinks: [['All pieces', '/boutique'], ['Made to order', '/sur-mesure'], ['New in', '/boutique?sort=newest']],
    infoLinks: [['Our story', '/a-propos'], ['Contact', '/contact'], ['Shipping & returns', '/livraison'], ['Terms of sale', '/cgv'], ['Legal notice', '/mentions-legales'], ['Privacy', '/confidentialite']],
    rights: 'Le Placard à Doudous — All rights reserved', secure: 'Secure payment by Stripe',
  },
};

const linkClass = 'font-sans text-sm font-medium text-[hsl(var(--foreground)/0.82)] underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline';
const headingClass = 'mb-4 font-sans text-[11px] font-extrabold uppercase tracking-[0.18em] text-foreground';

// La quatrième de couverture : même jaune beurre que la une, et le titre du
// magazine coupé par le bas de la page, pour refermer le numéro.
export default function Footer() {
  const c = useCopy(COPY);
  const { lp } = useLocale();
  return (
    <footer className="backcover relative overflow-hidden bg-[hsl(var(--brand-butter))] text-foreground">
      <div className="pastel-ribbon" aria-hidden="true" />
      <div className="relative z-[1] mx-auto max-w-7xl px-4 pt-16 sm:px-6 sm:pt-20 lg:px-10">
        {/* Promesse de marque */}
        <p className="max-w-3xl font-serif text-[2rem] font-extrabold leading-[1.02] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
          {c.promise[0]}<i className="font-medium text-primary">{c.promise[1]}</i>
        </p>

        <div className="mt-12 grid grid-cols-1 gap-10 border-t-2 border-foreground pt-10 sm:mt-14 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-3">
              <LogoMark className="h-12 w-10 shrink-0" aria-hidden="true" focusable="false" />
              <div>
                <p className="font-serif text-xl font-bold">Le Placard à Doudous</p>
                <p className="script-touch mt-1 text-[28px]">{c.by}</p>
              </div>
            </div>
            <p className="max-w-xs font-sans text-sm leading-relaxed text-[hsl(var(--foreground)/0.82)]">
              {c.about}
            </p>
            <p className="mt-6 inline-flex items-center gap-1.5 font-sans text-xs font-semibold text-[hsl(var(--foreground)/0.82)]">
              {c.madeWith} <Heart className="h-3 w-3 fill-primary text-primary" /> {c.inFrance}
            </p>
          </div>

          {/* Boutique */}
          <div>
            <p className={headingClass}>{c.shop}</p>
            <ul className="space-y-3">
              {c.shopLinks.filter(([, to]) => CUSTOM_ORDERS_ENABLED || to !== '/sur-mesure').map(([label, to]) => (
                <li key={to}><Link to={lp(to)} className={linkClass}>{label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Info */}
          <div>
            <p className={headingClass}>{c.info}</p>
            <ul className="space-y-3">
              {c.infoLinks.map(([label, to]) => (
                <li key={to}><Link to={lp(to)} className={linkClass}>{label}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-[hsl(var(--foreground)/0.2)] pt-8 sm:flex-row">
          <p className="inline-flex items-center gap-3 font-sans text-xs font-semibold text-[hsl(var(--foreground)/0.82)]">
            <span className="backcover-barcode" aria-hidden="true"><i /></span>
            © {new Date().getFullYear()} {c.rights}
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  className="text-[hsl(var(--foreground)/0.82)] transition-colors duration-200 hover:text-primary">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </a>
              ))}
            </div>
            <span className="font-sans text-xs font-semibold text-[hsl(var(--foreground)/0.82)]">{c.secure}</span>
          </div>
        </div>
      </div>
      <p className="backcover-masthead" aria-hidden="true">Doudous</p>
    </footer>
  );
}
